import { css } from '@emotion/react'
import {
  navTab,
  secondLevelNavTab,
  thirdLevelNavTab,
} from '@screens/Navigation/types'
import { useContext, useEffect, useRef, useState } from 'react'
import { MinimizedContext } from '@screens/Navigation/Navigation'
import {
  tabBaseStyle,
  minimizedTabStyle,
  minimizedPopupStyle,
} from '@screens/Navigation/components/TabStyle'
import { UISref } from '@components/UISref'
import * as React from 'react'
import { useRouter } from '@/react/hooks'
import { useAngularServices } from '@/react/components'
import { CurrentUserType } from '@/react/types/documentation'
import { createPortal } from 'react-dom'

import CloseIcon from '@/assets/icons/navigation/close-tab-icon.svg'
import OpenIcon from '@/assets/icons/navigation/open-tab-icon.svg'

interface StateChangeEvent {
  name: string
  params: Record<string, unknown>
}

export const Level1Tab = ({
  tabData,
  isOpen,
  isActive,
  onClick,
  onClose,
  openPopupIndex,
  setOpenPopupIndex,
  currentIndex,
  currentRoute,
}: {
  tabData: navTab
  isOpen: boolean
  isActive: boolean
  onClick: () => void
  onClose: () => void
  sidebarRef: React.RefObject<HTMLDivElement>
  openPopupIndex: number | null
  setOpenPopupIndex: (index: number | null) => void
  currentIndex: number
  currentRoute: string
}) => {
  const { CurrentUser, $rootScope } = useAngularServices()
  const { stateService } = useRouter()
  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color

  const minimized = useContext<boolean>(MinimizedContext)

  const [openTab1, setOpenTab1] = useState<number | null>(null)
  const [openTab2, setOpenTab2] = useState<number | null>(null)

  const tabRef = useRef(null)
  const minimizedTabRef = useRef<HTMLDivElement | null>(null)
  const popupRef = useRef<HTMLDivElement | null>(null)
  const [popupPos, setPopupPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  })

  const handleTabClick = () => {
    const specialRoutes = [
      'lessons.list',
      'assessment.assessment_manage',
      'index.dashboard',
    ]
    if (tabData.link && specialRoutes.includes(tabData.link)) {
      // Close all other menus
      onClose()
      setOpenTab1(null)
      setOpenTab2(null)
      setOpenPopupIndex(null)
    }
    onClick()
  }

  useEffect(() => {
    const unsubscribe = $rootScope.$on(
      '$stateChangeSuccess',
      (_event: unknown, toState: StateChangeEvent) => {
        const specialRoutes = [
          'lessons.list',
          'assessment.assessment_manage',
          'index.dashboard',
        ]
        if (specialRoutes.includes(toState.name)) {
          setOpenTab1(null)
          setOpenTab2(null)
          setOpenPopupIndex(null)
        }
      },
    )
    return () => unsubscribe()
  }, [$rootScope])

  useEffect(() => {
    const unsubscribe = $rootScope.$on('$stateChangeSuccess', () => {
      setOpenPopupIndex(null)
    })
    return () => unsubscribe()
  }, [$rootScope])

  useEffect(() => {
    if (tabData?.submenu?.length) {
      tabData.submenu.forEach((tab1, index1) => {
        if (
          tab1?.link === currentRoute &&
          String(getAppValue(tab1, tabData.app)) ===
            String(stateService.params.app)
        ) {
          setOpenTab1(index1)
        }
        if (tab1?.submenu?.length) {
          tab1.submenu.forEach((tab2, index2) => {
            if (
              tab2?.link === currentRoute &&
              String(getAppValue(tab2, tab1.app ?? tabData.app)) ===
                String(stateService.params.app)
            ) {
              setOpenTab1(index1)
              setOpenTab2(index2)
            }
          })
        }
      })
    }
  }, [currentRoute, stateService.params.app])

  const shouldShowPopup = openPopupIndex === currentIndex

  useEffect(() => {
    if (shouldShowPopup && minimizedTabRef.current) {
      const rect = minimizedTabRef.current.getBoundingClientRect()
      let top = rect.top
      const left = rect.right

      setTimeout(() => {
        if (popupRef.current) {
          const popupHeight = popupRef.current.offsetHeight
          const margin = 8 // some margin from the bottom
          if (top + popupHeight > window.innerHeight - margin) {
            top = Math.max(window.innerHeight - popupHeight - margin, 0)
          }
          setPopupPos({ top, left })
        }
      }, 0)

      setPopupPos({ top, left })
    }
  }, [shouldShowPopup])

  useEffect(() => {
    if (!shouldShowPopup) return

    function handleClickOutside(event: MouseEvent) {
      const popupNode = popupRef.current
      const tabNode = minimizedTabRef.current
      if (
        popupNode &&
        !popupNode.contains(event.target as Node) &&
        tabNode &&
        !tabNode.contains(event.target as Node)
      ) {
        setOpenPopupIndex(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [shouldShowPopup, setOpenPopupIndex])

  const handleSubmenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const getAppValue = (tab: navTab, parentApp?: string | number) =>
    (tab.params && tab.params.app) ??
    tab.app ??
    (parentApp !== undefined ? String(parentApp) : undefined)

  if (minimized)
    return (
      <section
        ref={tabRef}
        css={minimizedTabStyle(variableColor)}
        className="tab-level-1-identifier"
      >
        {tabData.link ? (
          <UISref to={tabData.link} params={tabData.params || {}}>
            <div
              ref={minimizedTabRef}
              className="display-segment tab-level-1-identifier"
              css={css({
                backgroundColor: isActive ? 'rgba(0,0,0,0.3)' : 'transparent',
              })}
              onMouseEnter={() => {
                setOpenPopupIndex(currentIndex)
              }}
              onClick={handleTabClick}
            >
              {tabData.icon && (
                <div className="image-holder tab-level-1-identifier">
                  {tabData.icon}
                </div>
              )}
            </div>
          </UISref>
        ) : (
          <div
            ref={minimizedTabRef}
            className="display-segment tab-level-1-identifier"
            css={css({
              backgroundColor: isActive ? 'rgba(0,0,0,0.3)' : 'transparent',
            })}
            onClick={() => {
              setOpenPopupIndex(
                openPopupIndex === currentIndex ? null : currentIndex,
              )
            }}
            onMouseEnter={() => {
              setOpenPopupIndex(currentIndex)
            }}
          >
            {tabData.icon && (
              <div className="image-holder tab-level-1-identifier">
                {tabData.icon}
              </div>
            )}
          </div>
        )}
        {shouldShowPopup &&
          (tabData.submenu?.length
            ? createPortal(
                <div
                  ref={popupRef}
                  className="tab-dropdown tab-level-1-identifier"
                  css={minimizedPopupStyle(variableColor)}
                  style={{
                    top: popupPos.top,
                    left: popupPos.left + 14,
                  }}
                  onClick={handleSubmenuClick}
                >
                  <div className="header">{tabData.text}</div>
                  {tabData.submenu.map((tabLevel2, index1) => {
                    if (!tabLevel2) return null
                    return (
                      <div
                        key={`${index1}-${tabLevel2.text}`}
                        className="tab-dropdown-1 tab-level-1-identifier"
                        onClick={handleSubmenuClick}
                      >
                        {tabLevel2.link ? (
                          <UISref
                            to={tabLevel2.link}
                            params={tabLevel2.params || {}}
                          >
                            <div
                              className="display-segment-2 segment-rounded tab-level-1-identifier"
                              onClick={(e) => {
                                handleSubmenuClick(e)
                                setOpenTab1(openTab1 === index1 ? null : index1)
                              }}
                              css={css({
                                backgroundColor:
                                  tabLevel2.link === currentRoute &&
                                  String(
                                    getAppValue(tabLevel2, tabData.app),
                                  ) === String(stateService.params.app)
                                    ? 'rgba(0,0,0,0.3)'
                                    : 'transparent',
                              })}
                            >
                              <div className="text-holder">
                                {tabLevel2.text}
                              </div>
                            </div>
                          </UISref>
                        ) : (
                          <div
                            className="display-segment-2 segment-rounded tab-level-1-identifier"
                            onClick={(e) => {
                              handleSubmenuClick(e)
                              setOpenTab1(openTab1 === index1 ? null : index1)
                            }}
                          >
                            <div className="text-holder tab-level-1-identifier">
                              {tabLevel2.text}
                            </div>
                          </div>
                        )}
                        {openTab1 === index1 && tabLevel2.submenu?.length && (
                          <div
                            className="tab-dropdown-1 tab-level-1-identifier"
                            onClick={handleSubmenuClick}
                          >
                            {tabLevel2.submenu.map((tabLevel3, index2) => {
                              if (!tabLevel3) return null
                              const isActive3 =
                                tabLevel3.link === currentRoute &&
                                String(
                                  getAppValue(
                                    tabLevel3,
                                    tabLevel2.app ?? tabData.app,
                                  ),
                                ) === String(stateService.params.app)
                              return (
                                <div
                                  key={`${index2}-${tabLevel3.text}`}
                                  className="tab-dropdown-1 tab-level-1-identifier"
                                  onClick={handleSubmenuClick}
                                >
                                  {tabLevel3.link ? (
                                    <UISref
                                      to={tabLevel3.link}
                                      params={tabLevel3.params || {}}
                                    >
                                      <div
                                        className="display-segment-3 segment-rounded tab-level-1-identifier"
                                        onClick={(e) => {
                                          handleSubmenuClick(e)
                                          setOpenTab2(
                                            openTab2 === index2 ? null : index2,
                                          )
                                        }}
                                        css={css({
                                          backgroundColor: isActive3
                                            ? 'rgba(0,0,0,0.3)'
                                            : 'transparent',
                                        })}
                                      >
                                        <div className="text-holder tab-level-1-identifier">
                                          {tabLevel3.text}
                                        </div>
                                      </div>
                                    </UISref>
                                  ) : (
                                    <div
                                      className="display-segment-3 segment-rounded tab-level-1-identifier"
                                      onClick={(e) => {
                                        handleSubmenuClick(e)
                                        setOpenTab2(
                                          openTab2 === index2 ? null : index2,
                                        )
                                      }}
                                    >
                                      <div className="text-holder tab-level-1-identifier">
                                        {tabLevel3.text}
                                      </div>
                                    </div>
                                  )}
                                  {openTab2 === index2 &&
                                    tabLevel3.submenu?.length && (
                                      <div className="tab-dropdown-2 tab-level-1-identifier">
                                        {tabLevel3.submenu.map((tabLevel4) => {
                                          if (!tabLevel4) return null
                                          return (
                                            <UISref
                                              key={tabLevel4.text}
                                              to={tabLevel4.link || ''}
                                              params={tabLevel4.params || {}}
                                            >
                                              <div
                                                className="display-segment-4 segment-rounded tab-level-1-identifier"
                                                css={css({
                                                  backgroundColor:
                                                    tabLevel4.link ===
                                                    currentRoute
                                                      ? 'rgba(0,0,0,0.3)'
                                                      : 'transparent',
                                                })}
                                              >
                                                {tabLevel4.text}
                                              </div>
                                            </UISref>
                                          )
                                        })}
                                      </div>
                                    )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>,
                document.body,
              )
            : createPortal(
                <div
                  ref={popupRef}
                  className="tab-dropdown tab-level-1-identifier"
                  css={minimizedPopupStyle(variableColor)}
                  style={{
                    top: popupPos.top,
                    left: popupPos.left + 14,
                  }}
                >
                  {/* <div className="header">{tabData.text}</div> */}
                  {tabData.link ? (
                    <UISref to={tabData.link} params={tabData.params || {}}>
                      <div
                        className="display-segment-2 segment-rounded tab-level-1-identifier"
                        css={css({
                          backgroundColor: isActive
                            ? 'rgba(0,0,0,0.3)'
                            : 'transparent',
                        })}
                      >
                        <div className="text-holder">{tabData.text}</div>
                      </div>
                    </UISref>
                  ) : (
                    <div className="display-segment-2 segment-rounded tab-level-1-identifier">
                      <div className="text-holder">{tabData.text}</div>
                    </div>
                  )}
                </div>,
                document.body,
              ))}
      </section>
    )

  return (
    <section
      ref={tabRef}
      css={tabBaseStyle()}
      className="tab-level-1-identifier"
      data-open={isOpen}
    >
      {tabData.link ? (
        <UISref to={tabData.link} params={tabData.params || {}}>
          <div
            className="display-segment segment-rounded tab-level-1-identifier"
            css={css({
              backgroundColor: isActive ? 'rgba(0,0,0,0.3)' : 'transparent',
            })}
            onClick={handleTabClick}
          >
            {tabData.icon && (
              <div className="image-holder tab-level-1-identifier">
                {tabData.icon}
              </div>
            )}
            <div className="text-holder tab-level-1-identifier">
              {tabData.text}
            </div>
          </div>
        </UISref>
      ) : (
        <div
          className="display-segment display-segment-icon segment-rounded tab-level-1-identifier"
          onClick={handleTabClick}
        >
          <div className="display-segment-2">
            {tabData.icon && (
              <div className="image-holder tab-level-1-identifier">
                {tabData.icon}
              </div>
            )}
            <div className="text-holder tab-level-1-identifier">
              {tabData.text}
            </div>
          </div>
          <div className="tab-level-1-identifier">
            {isOpen ? <img src={OpenIcon} /> : <img src={CloseIcon} />}
          </div>
        </div>
      )}
      {isOpen && tabData.submenu?.length && (
        <div className="tab-dropdown tab-level-1-identifier">
          {tabData.submenu.map((tabLevel2, index1) => {
            if (!tabLevel2) return null
            return (
              <div
                key={`${index1}-${tabLevel2.text}`}
                className="tab-dropdown-1 tab-level-1-identifier"
              >
                {tabLevel2.link ? (
                  <UISref to={tabLevel2.link} params={tabLevel2.params || {}}>
                    <div
                      className="display-segment-2 segment-rounded tab-level-1-identifier"
                      onClick={() => {
                        setOpenTab1(openTab1 === index1 ? null : index1)
                      }}
                      css={css({
                        backgroundColor:
                          tabLevel2.active?.includes(currentRoute) &&
                          String(getAppValue(tabLevel2, tabData.app)) ===
                            String(stateService.params.app)
                            ? 'rgba(0,0,0,0.3)'
                            : 'transparent',
                      })}
                    >
                      {tabLevel2.icon && (
                        <div className="image-holder tab-level-1-identifier">
                          {tabLevel2.icon}
                        </div>
                      )}
                      <div className="text-holder tab-level-1-identifier">
                        {tabLevel2.text}
                      </div>
                    </div>
                  </UISref>
                ) : (
                  <div
                    className="display-segment-2 display-segment-2-icon segment-rounded tab-level-1-identifier"
                    onClick={() => {
                      setOpenTab1(openTab1 === index1 ? null : index1)
                    }}
                  >
                    <div className="display-segment-2-content">
                      {tabLevel2.icon && (
                        <div className="image-holder tab-level-1-identifier">
                          {tabLevel2.icon}
                        </div>
                      )}
                      <div className="text-holder tab-level-1-identifier">
                        {tabLevel2.text}
                      </div>
                    </div>
                    <div className="tab-level-1-identifier">
                      {openTab1 === index1 ? (
                        <img src={OpenIcon} />
                      ) : (
                        <img src={CloseIcon} />
                      )}
                    </div>
                  </div>
                )}
                {openTab1 === index1 && tabLevel2.submenu?.length && (
                  <div className="tab-dropdown tab-level-1-identifier">
                    {tabLevel2.submenu.map((tabLevel3, index2) => {
                      if (!tabLevel3) return null
                      const isActive3 =
                        tabLevel3.active?.includes(currentRoute) &&
                        String(
                          getAppValue(tabLevel3, tabLevel2.app ?? tabData.app),
                        ) === String(stateService.params.app)
                      return (
                        <div
                          key={`${index2}-${tabLevel3.text}`}
                          className="tab-dropdown-2 tab-level-1-identifier"
                        >
                          {tabLevel3.link ? (
                            <UISref
                              to={tabLevel3.link}
                              params={tabLevel3.params || {}}
                            >
                              <div
                                className="display-segment-3 segment-rounded tab-level-1-identifier"
                                onClick={() => {
                                  setOpenTab2(
                                    openTab2 === index2 ? null : index2,
                                  )
                                }}
                                css={css({
                                  backgroundColor: isActive3
                                    ? 'rgba(0,0,0,0.3)'
                                    : 'transparent',
                                })}
                              >
                                <div className="text-holder tab-level-1-identifier">
                                  {tabLevel3.text}
                                </div>
                              </div>
                            </UISref>
                          ) : (
                            <div
                              className="display-segment-3 segment-rounded tab-level-1-identifier"
                              onClick={() => {
                                setOpenTab2(openTab2 === index2 ? null : index2)
                              }}
                            >
                              <div className="text-holder tab-level-1-identifier">
                                {tabLevel3.text}
                              </div>
                              <div className="tab-level-1-identifier">
                                {openTab2 === index2 ? (
                                  <img
                                    src={OpenIcon}
                                    className="tab-level-1-identifier"
                                  />
                                ) : (
                                  <img
                                    src={CloseIcon}
                                    className="tab-level-1-identifier"
                                  />
                                )}
                              </div>
                            </div>
                          )}
                          {openTab2 === index2 && tabLevel3.submenu?.length && (
                            <div className="tab-dropdown tab-level-1-identifier">
                              {tabLevel3.submenu.map((tabLevel4) => {
                                if (!tabLevel4) return null
                                return (
                                  <div
                                    key={tabLevel4.text}
                                    className="tab-dropdown-3 tab-level-1-identifier"
                                  >
                                    <UISref
                                      to={tabLevel4.link || ''}
                                      params={tabLevel4.params || {}}
                                    >
                                      <div
                                        className="display-segment-4 segment-rounded tab-level-1-identifier"
                                        css={css({
                                          backgroundColor:
                                            tabLevel4.active?.includes(
                                              currentRoute,
                                            ) &&
                                            String(
                                              getAppValue(
                                                tabLevel4,
                                                tabLevel3.app ?? tabData.app,
                                              ),
                                            ) ===
                                              String(stateService.params.app)
                                              ? 'rgba(0,0,0,0.3)'
                                              : 'transparent',
                                        })}
                                      >
                                        <div className="text-holder tab-level-1-identifier">
                                          {tabLevel4.text}
                                        </div>
                                      </div>
                                    </UISref>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
